/**
 * ============================================================================
 * MASTER PAGE: Create User - Multi-Step Wizard
 * ============================================================================
 * Design Style:
 *   Replicates the clean, executive-grade dual-card wizard aesthetic:
 *   - Left Sidebar Card with continuous vertical stepper (solid dark active circles,
 *     white hollow pending circles, connected vertical line, branding logo)
 *   - Right Main Card with "< Back" button, Step X/4 counter, bold step heading,
 *     horizontal divider line, 2-column split (left explanatory block with blue link,
 *     right form controls), and solid dark navy "Next" / "Create" button on bottom right.
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  userService,
  accountService,
  operatorService,
  countryService,
  roleService,
} from "../../services";
import { getCurrentUser, isSuperAdmin, isRegularUser } from "../../../core/utils/auth";
import { STORAGE_KEYS } from "../../../core/config/constants";

// ─── Icons ───────────────────────────────────────────────────────────────────
const ChevronLeftIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);
const ChevronDownIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>
);
const XMarkIcon = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);
const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);
const EyeIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);
const EyeSlashIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
  </svg>
);
const SparklesIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
  </svg>
);
const TrashIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

// ─── Step Configuration ───────────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: "Account Details", sub: "Company, API & billing quota" },
  { id: 2, label: "User Details", sub: "Credentials & role access" },
  { id: 3, label: "Country Access", sub: "Permitted messaging routes" },
  { id: 4, label: "Account Settings", sub: "Bot, IP & final confirmation" },
];

const POPULAR_COUNTRIES = ["IN", "US", "GB", "AE", "SA", "SG"];

function generateApiKey() {
  const bytes = new Uint8Array(20);
  window.crypto.getRandomValues(bytes);
  return "df_live_" + Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ─── Toggle Switch ────────────────────────────────────────────────────────────
function CleanToggle({ checked, onChange, label, desc }) {
  return (
    <div className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div>
        <p className="text-xs font-semibold text-[#0f172a] dark:text-white">{label}</p>
        {desc && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{desc}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none ${checked ? "bg-[#0f172a] dark:bg-blue-600" : "bg-gray-300 dark:bg-gray-600"
          }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-xs transition-transform duration-200 mt-0.5 ${checked ? "translate-x-4.5" : "translate-x-1"
            }`}
        />
      </button>
    </div>
  );
}

// ─── Country Tag Multi-Picker ─────────────────────────────────────────────────
function CountryTagPicker({ selected, onChange, allCountries }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = allCountries.filter(
    (c) =>
      !selected.find((s) => s.str_country_code === c.str_country_code) &&
      (c.str_country_name?.toLowerCase().includes(search.toLowerCase()) ||
        c.str_country_code?.toLowerCase().includes(search.toLowerCase()))
  );

  const addCountry = (c) => {
    onChange([...selected, c]);
    setSearch("");
  };

  const removeCountry = (code) => {
    onChange(selected.filter((s) => s.str_country_code !== code));
  };

  const selectPopular = () => {
    const populars = allCountries.filter((c) =>
      POPULAR_COUNTRIES.includes(c.str_country_code?.toUpperCase())
    );
    const combined = [...selected];
    populars.forEach((p) => {
      if (!combined.some((s) => s.str_country_code === p.str_country_code)) {
        combined.push(p);
      }
    });
    onChange(combined);
  };

  return (
    <div ref={containerRef} className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={selectPopular}
            className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
          >
            + Popular 6 (IN, US, GB, AE...)
          </button>
          <button
            type="button"
            onClick={() => onChange(allCountries)}
            className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
          >
            Select All ({allCountries.length})
          </button>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <span className="text-xs text-gray-400">
          {selected.length === 0 ? "Global unrestricted routing" : `${selected.length} destinations selected`}
        </span>
      </div>

      <div className="relative">
        <div
          className="min-h-[40px] flex items-center px-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 cursor-text focus-within:ring-2 focus-within:ring-[#0f172a]/20 focus-within:border-[#0f172a] transition-all"
          onClick={() => setOpen(true)}
        >
          <input
            type="text"
            className="w-full h-8 outline-none text-xs bg-transparent text-[#0f172a] dark:text-white placeholder:text-gray-400"
            placeholder="Type country name or dialing code to add..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
          />
        </div>

        {open && filtered.length > 0 && (
          <div className="absolute z-50 left-0 right-0 mt-1 max-h-44 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
            {filtered.slice(0, 40).map((c) => (
              <button
                key={c.str_country_code}
                type="button"
                className="w-full text-left px-3.5 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between"
                onClick={() => addCountry(c)}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-[11px] bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                    {c.str_country_code}
                  </span>
                  <span>{c.str_country_name}</span>
                </div>
                <span className="text-[10px] text-blue-600 font-semibold">+ Add</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="max-h-28 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-wrap gap-1.5">
          {selected.map((c) => (
            <span
              key={c.str_country_code}
              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-[#0f172a] dark:text-gray-200 text-xs font-medium shadow-2xs"
            >
              <span className="font-mono font-bold text-[10px] text-gray-500">{c.str_country_code}</span>
              <span className="truncate max-w-[130px]">{c.str_country_name}</span>
              <button
                type="button"
                onClick={() => removeCountry(c.str_country_code)}
                className="text-gray-400 hover:text-red-600 ml-0.5"
              >
                <XMarkIcon className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Account Tag Multi-Picker ─────────────────────────────────────────────────
function AccountTagPicker({ selected, onChange, allAccounts }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = allAccounts.filter(
    (a) =>
      !selected.find((s) => s.value === a.value) &&
      a.label.toLowerCase().includes(search.toLowerCase())
  );

  const addAccount = (acc) => {
    onChange([...selected, acc]);
    setSearch("");
  };

  const removeAccount = (val) => {
    onChange(selected.filter((s) => s.value !== val));
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className="min-h-[40px] max-h-24 overflow-y-auto flex flex-wrap items-center gap-1.5 p-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 cursor-text focus-within:ring-2 focus-within:ring-[#0f172a]/20 focus-within:border-[#0f172a] transition-all"
        onClick={() => setOpen(true)}
      >
        {selected.map((a) => (
          <span
            key={a.value}
            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-[#0f172a] dark:text-gray-200 text-xs font-medium"
          >
            {a.label}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeAccount(a.value);
              }}
              className="text-gray-400 hover:text-red-500"
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          className="flex-1 min-w-[120px] h-6 outline-none text-xs px-1 bg-transparent text-[#0f172a] dark:text-white placeholder:text-gray-400"
          placeholder={selected.length === 0 ? "Search & link messaging accounts..." : "Add more..."}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
          {filtered.slice(0, 30).map((a) => (
            <button
              key={a.value}
              type="button"
              className="w-full text-left px-3.5 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between"
              onClick={() => addAccount(a)}
            >
              <span>{a.label}</span>
              <span className="text-[10px] text-gray-400">Assign</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CreateUser() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);

  const [operators, setOperators] = useState([]);
  const [countries, setCountries] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);

  const [accountId, setAccountId] = useState(null);
  const [originalUsername, setOriginalUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState(null);
  const usernameTimer = useRef(null);

  // Step 1: Account
  const [account, setAccount] = useState({
    str_company_name: "",
    int_operator: "",
    str_api_key: generateApiKey(),
    int_current_credit: 0,
    dttm_credit_expire_on: "",
    str_country_id: "IN",
    str_status: "active",
  });

  // Step 2: User
  const [user, setUser] = useState({
    str_name: "",
    str_username: "",
    str_email: "",
    bool_email_as_username: false,
    str_mobile_no: "",
    str_password: "",
    str_confirm_password: "",
    str_role_id: "15", // Default User (Hierarchy 30)
    str_status: "active",
    bool_two_factor_enabled: false,
    linked_accounts: [],
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Step 3: Countries
  const [selectedCountries, setSelectedCountries] = useState([]);

  // Step 4: Settings
  const [settings, setSettings] = useState({
    bool_bot: false,
    bool_ip_whitelist_enabled: false,
    str_allow_ip_list: "",
    bool_chat_enabled: true, // Default: Enabled
    bool_indiamart_enabled: false, // Default: Disabled
    bool_image_camp_enabled: false, // Default: Disabled
    bool_tiny_url_enabled: false, // Default: Disabled
    bool_embedded_signup_enabled: false,
    int_embedded_signup_limit: 1,
    int_embedded_signup_used: 0,
  });

  // Sort and deduplicate roles hierarchy-wise (highest hierarchy first: 90 -> 75 ...)
  // SuperAdmin is excluded because no admin or superadmin creates SuperAdmin accounts here
  const hierarchySortedRoles = useMemo(() => {
    const rawList = Array.isArray(roles) && roles.length > 0
      ? [...roles]
      : [
          { int_role_id: 13, str_role_name: "Admin", int_role_hierarchy: 90 },
          { int_role_id: 14, str_role_name: "Reseller", int_role_hierarchy: 80 },
          { int_role_id: 5, str_role_name: "Company", int_role_hierarchy: 50 },
          { int_role_id: 6, str_role_name: "Account", int_role_hierarchy: 40 },
          { int_role_id: 15, str_role_name: "User", int_role_hierarchy: 30 },
          { int_role_id: 8, str_role_name: "Agent", int_role_hierarchy: 20 },
        ];

    // Exclude SuperAdmin roles
    const list = rawList.filter((r) => {
      const name = (r.str_role_name || "").toLowerCase().trim();
      const hier = Number(r.int_role_hierarchy ?? r.int_role_hirarchy ?? 0);
      const isSuper = name.includes("super") || hier >= 99 || String(r.int_role_id) === "1" || String(r.int_role_id) === "2";
      return !isSuper;
    });

    // Sort strictly descending by hierarchy
    list.sort((a, b) => {
      const hA = Number(a.int_role_hierarchy ?? a.int_role_hirarchy ?? 0);
      const hB = Number(b.int_role_hierarchy ?? b.int_role_hirarchy ?? 0);
      return hB - hA;
    });

    // Deduplicate duplicate role names (keeping the highest hierarchy tier)
    const seen = new Set();
    const result = [];
    for (const r of list) {
      const key = (r.str_role_name || "").trim().toLowerCase();
      if (!seen.has(key) || String(r.int_role_id) === String(user?.str_role_id)) {
        seen.add(key);
        result.push(r);
      }
    }
    return result;
  }, [roles, user?.str_role_id]);

  const getRoleOptionLabel = useCallback((role) => {
    const rawName = (role.str_role_name || "").trim();
    const lower = rawName.toLowerCase();

    let title = rawName.charAt(0).toUpperCase() + rawName.slice(1);
    if (lower === "admin") title = "Admin";
    else if (lower === "reseller") title = "Reseller";
    else if (lower === "company") title = "Company";
    else if (lower === "account") title = "Account";
    else if (lower === "user") title = "User";
    else if (lower === "agent") title = "Agent";

    const descriptors = {
      admin: "Platform Manager",
      reseller: "Multi-tenant Admin",
      company: "Tenant Company",
      account: "Client Account",
      user: "Standard Client",
      agent: "Support Agent",
    };

    const desc = descriptors[lower];
    if (desc) {
      return `${title} `;
    }
    return title;
  }, []);

  const getRoleDisplayName = useCallback(
    (roleId) => {
      const rId = String(roleId || "");
      const found = roles.find(
        (r) => String(r.int_role_id) === rId || String(r._id) === rId
      );
      if (found) {
        const raw = (found.str_role_name || "").trim();
        const lower = raw.toLowerCase();
        if (lower === "superadmin") return "Super Admin";
        return raw.charAt(0).toUpperCase() + raw.slice(1);
      }
      if (rId === "1") return "Super Admin";
      if (rId === "2" || rId === "3" || rId === "13") return "Admin";
      if (rId === "14") return "Reseller";
      if (rId === "5") return "Company";
      if (rId === "6") return "Account";
      if (rId === "4" || rId === "7" || rId === "15") return "User";
      if (rId === "8") return "Agent";
      return rId ? `Role ${rId}` : "—";
    },
    [roles]
  );

  // Load Metadata & (if editing) User Details
  useEffect(() => {
    let isMounted = true;
    const initializeData = async () => {
      try {
        const currentLoggedIn = getCurrentUser();
        if (isRegularUser(currentLoggedIn)) {
          Swal.fire({
            title: "Access Denied",
            text: "Client users do not have administrative permissions to create or edit accounts.",
            icon: "warning",
            confirmButtonColor: "#0f172a",
          });
          navigate("/admin/users");
          return;
        }

        if (isEdit) setLoadingUser(true);

        const [opRes, cntRes, roleRes, accRes] = await Promise.all([
          operatorService.get({ limit: 100 }),
          countryService.get({ limit: 300 }),
          roleService.get({ limit: 100, sortBy: "int_role_hierarchy", sortOrder: "desc" }),
          accountService.get({ limit: 200 }),
        ]);

        const loadedCountries = cntRes?.success ? cntRes.data || [] : [];
        const rawAccounts = accRes?.success ? accRes.data || [] : [];
        const loadedAccounts = rawAccounts.map((a) => ({
          value: String(a.int_account_id),
          label: `ACC${String(a.int_account_id).padStart(6, "0")}${a.str_company_name ? " – " + a.str_company_name : ""
            }`,
        }));

        if (isMounted) {
          if (opRes?.success) setOperators(opRes.data || []);
          setCountries(loadedCountries);
          const loadedRoles = roleRes?.success ? roleRes.data || [] : [];
          setRoles(loadedRoles);
          setAllAccounts(loadedAccounts);

          // If creating new user, dynamically resolve User role ID from backend roles
          if (!isEdit) {
            const foundUserRole = loadedRoles.find(
              (r) => (r.str_role_name || "").trim().toLowerCase() === "user"
            );
            if (foundUserRole) {
              const uRoleId = String(foundUserRole.int_role_id ?? foundUserRole._id);
              setUser((prev) => ({
                ...prev,
                str_role_id: prev.str_role_id === "4" || prev.str_role_id === "15" || !prev.str_role_id ? uRoleId : prev.str_role_id,
              }));
            }
          }
        }

        // Fetch User and Linked Account for Edit Mode
        if (isEdit && id) {
          const userRes = await userService.view(id);
          if (!userRes?.success || !userRes.data) {
            Swal.fire({
              title: "User Not Found",
              text: "Could not find user details.",
              icon: "error",
              confirmButtonColor: "#0f172a",
            });
            navigate("/admin/users");
            return;
          }

          const u = userRes.data;

          // Guard: Prohibit non-superadmins from viewing or editing the SuperAdmin account
          const isTargetSuper =
            u.str_username === "superadmin" ||
            String(u.str_role_id) === "1" ||
            (u.str_name && u.str_name.toLowerCase().includes("super administrator"));

          const currentLoggedIn = getCurrentUser();
          const isCurrentSuper = isSuperAdmin(currentLoggedIn);

          if (isTargetSuper && !isCurrentSuper) {
            Swal.fire({
              title: "Access Denied",
              text: "Administrators do not have permission to view or edit the Super Administrator account.",
              icon: "error",
              confirmButtonColor: "#0f172a",
            });
            navigate("/admin/users");
            return;
          }

          if (isMounted) {
            setOriginalUsername(u.str_username || "");

            // Pre-fill user data
            setUser({
              str_name: u.str_name || "",
              str_username: u.str_username || "",
              str_email: u.str_email || "",
              bool_email_as_username: false,
              str_mobile_no: u.str_mobile_no || "",
              str_password: "",
              str_confirm_password: "",
              str_role_id: String(
                u.str_role_id === "4" || u.str_role_id === "7"
                  ? "15"
                  : (u.str_role_id || "15")
              ),
              str_status: (u.str_status || "active").toLowerCase(),
              bool_two_factor_enabled: Boolean(u.bool_two_factor_enabled),
              linked_accounts: Array.isArray(u.linked_accounts)
                ? u.linked_accounts.map((accVal) => {
                  const found = loadedAccounts.find((x) => x.value === String(accVal));
                  return found || {
                    value: String(accVal),
                    label: `ACC${String(accVal).padStart(6, "0")}`,
                  };
                })
                : [],
            });

            // Pre-fill account data
            let accDoc = null;
            let targetAccId = u.str_account_id;

            if (targetAccId) {
              try {
                const accResItem = await accountService.view(targetAccId);
                if (accResItem?.success && accResItem.data) {
                  accDoc = accResItem.data;
                }
              } catch (accErr) {
                console.warn("Could not fetch account directly by id:", accErr);
              }
            }

            // Fallback match from raw accounts list
            if (!accDoc && rawAccounts.length > 0) {
              accDoc = rawAccounts.find((a) => {
                if (targetAccId && (String(a.int_account_id) === String(targetAccId) || String(a._id) === String(targetAccId))) {
                  return true;
                }
                if (a.assigned_user && (String(a.assigned_user._id) === String(u._id) || String(a.assigned_user.int_user_id) === String(u.int_user_id))) {
                  return true;
                }
                return false;
              });
            }

            if (accDoc) {
              setAccountId(accDoc.int_account_id || accDoc._id);
              setAccount({
                str_company_name: accDoc.str_company_name || u.str_name || "",
                int_operator: accDoc.int_operator ? String(accDoc.int_operator) : "",
                str_api_key: accDoc.str_api_key || generateApiKey(),
                int_current_credit: accDoc.int_current_credit ?? 0,
                dttm_credit_expire_on: accDoc.dttm_credit_expire_on
                  ? String(accDoc.dttm_credit_expire_on).substring(0, 10)
                  : "",
                str_country_id: accDoc.str_country_id || "IN",
                str_status: (accDoc.str_status || "active").toLowerCase(),
              });

              setSettings({
                bool_bot: Boolean(accDoc.bool_bot),
                bool_ip_whitelist_enabled: Boolean(accDoc.bool_ip_whitelist_enabled),
                str_allow_ip_list: accDoc.str_allow_ip_list || "",
                bool_chat_enabled: u.bool_chat_enabled !== false,
                bool_indiamart_enabled: Boolean(u.bool_indiamart_enabled),
                bool_image_camp_enabled: Boolean(u.bool_image_camp_enabled),
                bool_tiny_url_enabled: Boolean(u.bool_tiny_url_enabled),
                bool_embedded_signup_enabled: Boolean(u.bool_embedded_signup_enabled),
                int_embedded_signup_limit: Number(u.int_embedded_signup_limit || 0),
                int_embedded_signup_used: Number(u.int_embedded_signup_used || 0),
              });

              if (Array.isArray(accDoc.allowed_countries) && accDoc.allowed_countries.length > 0) {
                const matchedCountries = accDoc.allowed_countries.map((cCode) => {
                  const match = loadedCountries.find((c) => c.str_country_code === cCode);
                  return match || { str_country_code: cCode, str_country_name: cCode };
                });
                setSelectedCountries(matchedCountries);
              }
            } else {
              setAccount((prev) => ({
                ...prev,
                str_company_name: u.str_name || "",
              }));
            }
          }
        }
      } catch (e) {
        console.error("Failed to initialize CreateUser data:", e);
      } finally {
        if (isMounted) setLoadingUser(false);
      }
    };

    initializeData();
    return () => {
      isMounted = false;
    };
  }, [id, isEdit, navigate]);

  const checkUsername = useCallback(
    async (username) => {
      if (!username || username.trim().length < 3) {
        setUsernameStatus(null);
        return;
      }
      if (isEdit && originalUsername && username.trim().toLowerCase() === originalUsername.toLowerCase()) {
        setUsernameStatus("available");
        return;
      }
      setUsernameStatus("checking");
      try {
        const res = await userService.get({ search: username.trim(), limit: 5 });
        const taken = (res.data || []).some(
          (u) =>
            u.str_username?.toLowerCase() === username.trim().toLowerCase() &&
            String(u._id) !== String(id)
        );
        setUsernameStatus(taken ? "taken" : "available");
      } catch {
        setUsernameStatus(null);
      }
    },
    [isEdit, originalUsername, id]
  );

  const handleUsernameChange = (val) => {
    setUser((p) => ({ ...p, str_username: val }));
    clearTimeout(usernameTimer.current);
    usernameTimer.current = setTimeout(() => checkUsername(val), 500);
  };

  useEffect(() => {
    if (user.bool_email_as_username) {
      const localPart = user.str_email.split("@")[0] || "";
      setUser((p) => ({ ...p, str_username: localPart }));
      clearTimeout(usernameTimer.current);
      if (localPart) {
        usernameTimer.current = setTimeout(() => checkUsername(localPart), 500);
      } else {
        setUsernameStatus(null);
      }
    }
  }, [user.bool_email_as_username, user.str_email, checkUsername]);

  const validateStep = () => {
    if (step === 1) {
      if (!account.str_company_name.trim()) {
        Swal.fire({ title: "Required", text: "Please enter Company / Account Name.", icon: "warning", confirmButtonColor: "#0f172a" });
        return false;
      }
    }
    if (step === 2) {
      if (!user.str_name.trim()) {
        Swal.fire({ title: "Required", text: "Please enter Full Name.", icon: "warning", confirmButtonColor: "#0f172a" });
        return false;
      }
      if (!user.str_username.trim()) {
        Swal.fire({ title: "Required", text: "Please enter Username.", icon: "warning", confirmButtonColor: "#0f172a" });
        return false;
      }
      if (usernameStatus === "taken") {
        Swal.fire({ title: "Username Taken", text: "This username is already taken. Please choose another.", icon: "error", confirmButtonColor: "#0f172a" });
        return false;
      }
      if (!user.str_email.trim() || !user.str_email.includes("@")) {
        Swal.fire({ title: "Invalid Email", text: "Please enter a valid Email Address.", icon: "warning", confirmButtonColor: "#0f172a" });
        return false;
      }
      // Password required only on new creation; optional on edit
      if (!isEdit && !user.str_password) {
        Swal.fire({ title: "Required", text: "Please set a password.", icon: "warning", confirmButtonColor: "#0f172a" });
        return false;
      }
      if (user.str_password) {
        if (user.str_password.length < 6) {
          Swal.fire({ title: "Password Too Short", text: "Password must be at least 6 characters long.", icon: "warning", confirmButtonColor: "#0f172a" });
          return false;
        }
        if (user.str_password !== user.str_confirm_password) {
          Swal.fire({ title: "Passwords Mismatch", text: "Passwords do not match.", icon: "warning", confirmButtonColor: "#0f172a" });
          return false;
        }
      }
    }
    return true;
  };

  const goNext = () => {
    if (validateStep()) setStep((s) => Math.min(4, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmit = async () => {
    if (!validateStep()) return;

    if (isEdit && originalUsername === "superadmin" && !isSuperAdmin(getCurrentUser())) {
      Swal.fire({
        title: "Access Denied",
        text: "Only Super Administrators can modify this account.",
        icon: "error",
        confirmButtonColor: "#0f172a",
      });
      return;
    }

    const confirmRes = await Swal.fire({
      title: isEdit ? "Save Changes?" : "Provision New Account?",
      html: `
        <div style="text-align:left;font-size:13px;line-height:1.7;">
          <p><b>Account:</b> ${account.str_company_name}</p>
          <p><b>User:</b> ${user.str_name} (@${user.str_username})</p>
          <p><b>Role:</b> ${getRoleDisplayName(user.str_role_id)}</p>
          <p><b>${isEdit ? "Wallet Balance" : "Initial Credits"}:</b> ₹ ${Number(account.int_current_credit || 0).toLocaleString("en-IN")}</p>
          <p><b>Country Access:</b> ${selectedCountries.length > 0 ? selectedCountries.map(c => c.str_country_code).join(", ") : "All (Unrestricted)"}</p>
          ${isEdit && !user.str_password ? "<p style='color:#64748b;font-size:11px;margin-top:6px;'><i>Password will remain unchanged.</i></p>" : ""}
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Save Changes" : "Confirm & Provision",
      cancelButtonText: "Review Data",
      confirmButtonColor: "#0f172a",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!confirmRes.isConfirmed) return;

    setSubmitting(true);
    try {
      const loggedUser = getCurrentUser();
      const userPayload = {
        str_name: user.str_name.trim(),
        str_username: user.str_username.trim(),
        str_email: user.str_email.trim(),
        str_mobile_no: user.str_mobile_no.trim(),
        str_role_id: user.str_role_id,
        str_status: user.str_status,
        bool_is_active: user.str_status === "active",
        bool_two_factor_enabled: Boolean(user.bool_two_factor_enabled),
        bool_chat_enabled: settings.bool_chat_enabled !== false,
        bool_indiamart_enabled: Boolean(settings.bool_indiamart_enabled),
        bool_image_camp_enabled: Boolean(settings.bool_image_camp_enabled),
        bool_tiny_url_enabled: Boolean(settings.bool_tiny_url_enabled),
        bool_embedded_signup_enabled: Boolean(settings.bool_embedded_signup_enabled),
        int_embedded_signup_limit: Number(settings.int_embedded_signup_limit || 0),
        linked_accounts: (user.linked_accounts || []).map((a) => a.value),
        int_created_by: loggedUser?.int_user_id || 0,
        str_created_by: loggedUser?._id ? String(loggedUser._id) : (loggedUser?.str_username || ""),
      };

      if (user.str_password) {
        userPayload.str_password = user.str_password;
      }

      if (isEdit) {
        // Update User
        const userRes = await userService.edit(id, userPayload);
        if (!userRes.success) throw new Error(userRes.error || "Failed to update user account.");

        // Update or Create Account
        const accountPayload = {
          str_company_name: account.str_company_name.trim(),
          int_operator: account.int_operator ? Number(account.int_operator) : 0,
          str_api_key: account.str_api_key,
          dttm_credit_expire_on: account.dttm_credit_expire_on || null,
          str_country_id: account.str_country_id || "IN",
          str_status: account.str_status || "active",
          allowed_countries: selectedCountries.map((c) => c.str_country_code),
          bool_bot: settings.bool_bot,
          bool_ip_whitelist_enabled: settings.bool_ip_whitelist_enabled,
          str_allow_ip_list: settings.str_allow_ip_list,
          assigned_user_id: id,
        };

        if (accountId) {
          // Do not overwrite live wallet balance when editing account details
          await accountService.edit(accountId, accountPayload);
        } else {
          accountPayload.int_current_credit = Number(account.int_current_credit) || 0;
          const accRes = await accountService.create(accountPayload);
          if (accRes.data?.int_account_id) {
            await userService.edit(id, {
              str_account_id: String(accRes.data.int_account_id),
            });
          }
        }

        if (loggedUser && (loggedUser._id === id || String(loggedUser.int_user_id) === String(id))) {
          const updatedUser = {
            ...loggedUser,
            bool_chat_enabled: userPayload.bool_chat_enabled,
            bool_indiamart_enabled: userPayload.bool_indiamart_enabled,
            bool_image_camp_enabled: userPayload.bool_image_camp_enabled,
            bool_tiny_url_enabled: userPayload.bool_tiny_url_enabled,
          };
          localStorage.setItem(STORAGE_KEYS?.USER || "user", JSON.stringify(updatedUser));
          window.dispatchEvent(new Event("storage"));
          window.dispatchEvent(new Event("user-permissions-changed"));
        }

        await Swal.fire({
          title: "User Updated!",
          text: `User "${user.str_name}" & Account details saved successfully.`,
          icon: "success",
          confirmButtonColor: "#0f172a",
        });
      } else {
        // Create User
        const userRes = await userService.create(userPayload);
        if (!userRes.success) throw new Error(userRes.error || "Failed to create user account.");
        const createdUserId = userRes.data?._id;

        // Create Account
        const accountPayload = {
          str_company_name: account.str_company_name.trim(),
          int_operator: account.int_operator ? Number(account.int_operator) : 0,
          str_api_key: account.str_api_key,
          int_current_credit: Number(account.int_current_credit) || 0,
          dttm_credit_expire_on: account.dttm_credit_expire_on || null,
          str_country_id: account.str_country_id || "IN",
          str_status: account.str_status || "active",
          allowed_countries: selectedCountries.map((c) => c.str_country_code),
          bool_bot: settings.bool_bot,
          bool_ip_whitelist_enabled: settings.bool_ip_whitelist_enabled,
          str_allow_ip_list: settings.str_allow_ip_list,
          assigned_user_id: createdUserId,
        };

        const accRes = await accountService.create(accountPayload);
        if (!accRes.success) throw new Error(accRes.error || "Failed to create messaging account.");

        if (accRes.data?.int_account_id && createdUserId) {
          await userService.edit(createdUserId, {
            str_account_id: String(accRes.data.int_account_id),
          });
        }

        await Swal.fire({
          title: "Account Provisioned!",
          text: `User "${user.str_name}" & Account "${account.str_company_name}" created successfully.`,
          icon: "success",
          confirmButtonColor: "#0f172a",
        });
      }

      navigate("/admin/users");
    } catch (err) {
      Swal.fire({
        title: isEdit ? "Update Failed" : "Creation Failed",
        text: err.message || "Failed to save account.",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/users");
  };

  const handleDeleteUser = async () => {
    if (
      originalUsername === "superadmin" ||
      (user.str_role_id === "1" && (user.str_name || "").toLowerCase().includes("super"))
    ) {
      Swal.fire({
        title: "Action Not Allowed",
        text: "The root SuperAdmin account cannot be deleted.",
        icon: "warning",
        confirmButtonColor: "#0f172a",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Delete User Account?",
      html: `
        <div style="font-size:13px;color:#475569;line-height:1.6;">
          Are you sure you want to delete user <b>${user.str_name || user.str_username}</b> (@${originalUsername || user.str_username})?
          
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete User",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setSubmitting(true);
    try {
      const res = await userService.delete(id);
      if (res?.success === false) {
        throw new Error(res.error || "Failed to delete user account.");
      }

      await Swal.fire({
        title: "User Deleted!",
        text: `User "${user.str_name || user.str_username}" has been deactivated and soft-deleted.`,
        icon: "success",
        confirmButtonColor: "#0f172a",
      });

      navigate("/admin/users");
    } catch (err) {
      Swal.fire({
        title: "Delete Failed",
        text: err.message || "Could not delete user account.",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingUser) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-white dark:bg-gray-900">
        <div className="w-9 h-9 border-3 border-[#0f172a] dark:border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Loading user details...</p>
        <p className="text-xs text-gray-400 mt-1">Fetching account information and credentials</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex bg-white dark:bg-gray-900 overflow-hidden min-h-0">

      {/* ── Left Stepper Sidebar (Flush, no border-radius) ── */}
      <div className="w-72 flex-shrink-0 bg-[#f8fafc] dark:bg-gray-950 border-r border-gray-200/80 dark:border-gray-800 p-6 flex flex-col justify-between h-full">
        <div>
          {/* Top Brand Logo */}


          {/* Stepper with Continuous Connecting Vertical Line */}
          <div className="relative pl-1">
            {STEPS.map((s, idx) => {
              const active = step === s.id;
              const done = step > s.id;

              return (
                <div key={s.id} className="relative flex items-start gap-3.5 pb-8 last:pb-0">
                  {/* Vertical connecting line running down behind circles */}
                  {idx < STEPS.length - 1 && (
                    <div
                      className={`absolute left-[13px] top-[26px] bottom-0 w-[2px] ${done ? "bg-[#0f172a] dark:bg-white" : "bg-gray-200 dark:bg-gray-700"
                        } transition-colors duration-200`}
                    />
                  )}

                  {/* Step Circle */}
                  <button
                    type="button"
                    disabled={!done && !active}
                    onClick={() => done && setStep(s.id)}
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold z-10 transition-all flex-shrink-0 ${active || done
                      ? "bg-[#0f172a] dark:bg-white text-white dark:text-gray-900 shadow-xs"
                      : "bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-500 font-semibold"
                      } ${done ? "cursor-pointer hover:opacity-80" : ""}`}
                  >
                    {done ? <CheckIcon className="w-3.5 h-3.5" /> : s.id}
                  </button>

                  {/* Step Label & Subtitle */}
                  <div className="pt-0.5 min-w-0">
                    <p
                      className={`text-sm font-bold leading-tight ${active
                        ? "text-[#0f172a] dark:text-white"
                        : done
                          ? "text-gray-700 dark:text-gray-300"
                          : "text-gray-400 dark:text-gray-500"
                        }`}
                    >
                      {s.label}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">
                      {s.sub}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Help Tip */}
        <div className="pt-4 border-t border-gray-150 dark:border-gray-800">
          <p className="text-[11px] text-gray-400 leading-relaxed">
            Need guidance? Complete the 4 steps to initialize your isolated MongoDB tenant instance.
          </p>
        </div>
      </div>

      {/* ── Right Main Area (Flush, no border-radius) ── */}
      <div className="flex-1 bg-white dark:bg-gray-900 p-6 sm:p-8 flex flex-col justify-between h-full min-w-0 overflow-hidden">

        {/* ── Top Header Section ── */}
        <div className="flex-shrink-0">
          {/* Top Row: < Back Button & Action Pill */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={step === 1 ? handleCancel : goBack}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold shadow-2xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            >
              <ChevronLeftIcon className="w-3.5 h-3.5" />
              Back
            </button>


            <div className="flex items-center gap-2.5">
              {isEdit && (
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 rounded-lg text-xs font-bold hover:bg-red-100 dark:hover:bg-red-900/60 transition-colors cursor-pointer"
                  title="Soft-delete this user account"
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                  Delete User
                </button>
              )}

              {step === 2 && (
                <span className="text-xs text-gray-400 font-medium">
                  {user.bool_email_as_username ? "Username linked to Email" : "Custom Username"}
                </span>
              )}
              {step === 3 && (
                <span className="text-xs text-gray-400 font-medium">
                  {selectedCountries.length > 0 ? `${selectedCountries.length} countries selected` : "All destinations open"}
                </span>
              )}
              {step === 4 && (
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                  Final Step
                </span>
              )}
            </div>
          </div>

          {/* Step Number & Big Heading */}
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium text-gray-400 tracking-wide">
                Step {step}/4
              </p>
              {isEdit && (
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                  Editing: @{originalUsername}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] dark:text-white tracking-tight mt-0.5">
              {STEPS[step - 1].label}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {isEdit
                ? `Update ${STEPS[step - 1].label.toLowerCase()} for this user and account`
                : `Provide your ${STEPS[step - 1].label.toLowerCase()} information below`}
            </p>
          </div>

          {/* Full-width Divider Line */}
          <div className="border-b border-gray-150 dark:border-gray-800 my-4 sm:my-5" />
        </div>

        {/* ── 2-Column Body Section (Signature Reference Style) ── */}
        <div className="flex-1 overflow-y-auto min-h-0 flex flex-col md:flex-row gap-6 md:gap-10 pr-1">

          {/* Left Explanatory Sub-Column (~32% width) */}


          {/* Right Form Fields Sub-Column (~68% width) */}
          <div className="flex-1 min-w-0">

            {/* ════ STEP 1: Account Details ════ */}
            {step === 1 && (
              <div className="space-y-3.5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Company Name */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Company / Account Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Acme Corporation"
                      value={account.str_company_name}
                      onChange={(e) => setAccount((p) => ({ ...p, str_company_name: e.target.value }))}
                      className="w-full h-10 px-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                    />
                  </div>

                  {/* Telephony Route */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Operator
                    </label>
                    <select
                      value={account.int_operator}
                      onChange={(e) => setAccount((p) => ({ ...p, int_operator: e.target.value }))}
                      className="w-full h-10 px-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                    >
                      <option value="">-- Direct / Default Route --</option>
                      {operators.map((op) => (
                        <option key={op.int_operator_id || op._id} value={op.int_operator_id}>
                          {op.str_operator}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* API Key Token */}
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      API Key Token
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        readOnly
                        value={account.str_api_key}
                        className="w-full h-10 px-3.5 pr-24 font-mono text-xs bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setAccount((p) => ({ ...p, str_api_key: generateApiKey() }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-blue-600 hover:text-blue-800 px-2 py-1 rounded cursor-pointer"
                      >
                        Regenerate
                      </button>
                    </div>
                  </div>

                  {/* Wallet Credits */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                        {isEdit ? "Wallet Credits" : "Initial Wallet Credits"}
                      </label>
                      {isEdit && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                          <svg className="w-3 h-3 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                          </svg>
                          Non-editable (Ledger Managed)
                        </span>
                      )}
                    </div>

                    {isEdit ? (
                      <div>
                        <div className="relative flex items-center">
                          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-emerald-600 dark:text-emerald-400 font-bold text-base">
                            ₹
                          </div>
                          <input
                            type="text"
                            readOnly
                            disabled
                            value={`${Number(account.int_current_credit || 0).toLocaleString("en-IN")} Credits`}
                            className="w-full h-10 pl-8 pr-28 bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-bold font-mono text-[#0f172a] dark:text-white cursor-not-allowed select-none opacity-90"
                            title="Wallet credits cannot be directly modified here. Use Wallet & Ledgers to add, transfer, or deduct balance."
                          />
                          <button
                            type="button"
                            onClick={() => navigate("/admin/ledgers")}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-[#3a57e8] hover:bg-[#2b44c7] text-white shadow-xs transition-colors flex items-center gap-1 cursor-pointer"
                            title="Open Wallet & Ledgers to add or transfer balance"
                          >
                            <span>Ledger</span>
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                            </svg>
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                          Credits are managed via <button type="button" onClick={() => navigate("/admin/ledgers")} className="text-[#3a57e8] hover:underline font-semibold cursor-pointer">Wallet & Ledgers</button> with full transaction auditing.
                        </p>
                      </div>
                    ) : (
                      <div className="relative flex items-center">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-emerald-600 dark:text-emerald-400 font-bold text-base">
                          ₹
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={account.int_current_credit}
                          onChange={(e) => setAccount((p) => ({ ...p, int_current_credit: e.target.value }))}
                          placeholder="0"
                          className="w-full h-10 pl-8 pr-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold font-mono text-[#0f172a] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                        />
                      </div>
                    )}
                  </div>

                  {/* Default Country Code */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Default Country Code
                    </label>
                    <input
                      type="text"
                      maxLength={4}
                      placeholder="IN"
                      value={account.str_country_id}
                      onChange={(e) => setAccount((p) => ({ ...p, str_country_id: e.target.value.toUpperCase() }))}
                      className="w-full h-10 px-3.5 uppercase font-mono bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                    />
                  </div>

                  {/* Credit Expiry Date */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Credit Expiry Date
                      </label>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => setAccount((p) => ({ ...p, dttm_credit_expire_on: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10) }))}
                          className="px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 rounded"
                        >
                          +30d
                        </button>
                        <button
                          type="button"
                          onClick={() => setAccount((p) => ({ ...p, dttm_credit_expire_on: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10) }))}
                          className="px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 rounded"
                        >
                          +90d
                        </button>
                        <button
                          type="button"
                          onClick={() => setAccount((p) => ({ ...p, dttm_credit_expire_on: "" }))}
                          className="px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 rounded"
                        >
                          Never
                        </button>
                      </div>
                    </div>
                    <input
                      type="date"
                      value={account.dttm_credit_expire_on}
                      onChange={(e) => setAccount((p) => ({ ...p, dttm_credit_expire_on: e.target.value }))}
                      className="w-full h-10 px-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                    />
                  </div>

                  {/* Account Status */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Account Status
                    </label>
                    <select
                      value={account.str_status}
                      onChange={(e) => setAccount((p) => ({ ...p, str_status: e.target.value }))}
                      className="w-full h-10 px-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                    >
                      <option value="active">Active (Full access)</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* ════ STEP 2: User Details ════ */}
            {step === 2 && (
              <div className="space-y-3.5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. John Doe"
                      value={user.str_name}
                      onChange={(e) => setUser((p) => ({ ...p, str_name: e.target.value }))}
                      className="w-full h-10 px-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                    />
                  </div>

                  {/* Email Address with Inline Checkbox */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={user.bool_email_as_username}
                          onChange={(e) => setUser((p) => ({ ...p, bool_email_as_username: e.target.checked }))}
                          className="w-3.5 h-3.5 rounded accent-[#0f172a]"
                        />
                        <span className="text-[11px] text-gray-500 hover:text-gray-800">
                          Use as username
                        </span>
                      </label>
                    </div>
                    <input
                      type="email"
                      placeholder="e.g. john@example.com"
                      value={user.str_email}
                      onChange={(e) => setUser((p) => ({ ...p, str_email: e.target.value }))}
                      className="w-full h-10 px-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                    />
                  </div>

                  {/* Username with Live Availability Indicator */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Username <span className="text-red-500">*</span>
                      </label>
                      {usernameStatus === "available" && (
                        <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-0.5">
                          ✓ Available
                        </span>
                      )}
                      {usernameStatus === "taken" && (
                        <span className="text-[11px] font-semibold text-red-500 flex items-center gap-0.5">
                          ✗ Username Taken
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="e.g. johndoe"
                        value={user.str_username}
                        disabled={user.bool_email_as_username}
                        onChange={(e) => handleUsernameChange(e.target.value)}
                        className={`w-full h-10 px-3.5 pr-8 rounded-lg outline-none text-sm transition-all ${user.bool_email_as_username
                          ? "bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed"
                          : usernameStatus === "available"
                            ? "bg-white border border-emerald-400 focus:ring-2 focus:ring-emerald-400/20"
                            : usernameStatus === "taken"
                              ? "bg-white border border-red-400 focus:ring-2 focus:ring-red-400/20"
                              : "bg-white border border-gray-200 focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a]"
                          }`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        {usernameStatus === "checking" && (
                          <div className="w-3.5 h-3.5 border-2 border-[#0f172a] border-t-transparent rounded-full animate-spin" />
                        )}
                        {usernameStatus === "available" && (
                          <span className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                            <CheckIcon className="w-3 h-3" />
                          </span>
                        )}
                        {usernameStatus === "taken" && (
                          <span className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                            <XMarkIcon className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Number */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      placeholder="e.g. +91 98765 43210"
                      value={user.str_mobile_no}
                      onChange={(e) => setUser((p) => ({ ...p, str_mobile_no: e.target.value }))}
                      className="w-full h-10 px-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                    />
                  </div>

                  {/* Assign Role */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      
Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={String(user.str_role_id || "")}
                      onChange={(e) => setUser((p) => ({ ...p, str_role_id: e.target.value }))}
                      className="w-full h-10 px-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                    >
                      {hierarchySortedRoles.map((r) => (
                        <option key={r._id || r.int_role_id} value={String(r.int_role_id)}>
                          {getRoleOptionLabel(r)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* User Status */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      User Status
                    </label>
                    <select
                      value={user.str_status}
                      onChange={(e) => setUser((p) => ({ ...p, str_status: e.target.value }))}
                      className="w-full h-10 px-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Password{" "}
                      {isEdit ? (
                        <span className="text-gray-400 font-normal">(Leave blank to keep unchanged)</span>
                      ) : (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder={isEdit ? "Enter new password (optional)" : "Min. 6 characters"}
                        value={user.str_password}
                        onChange={(e) => setUser((p) => ({ ...p, str_password: e.target.value }))}
                        className="w-full h-10 px-3.5 pr-9 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-[#0f172a] dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a] transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Confirm Password{" "}
                        {isEdit ? (
                          <span className="text-gray-400 font-normal">(If changing)</span>
                        ) : (
                          <span className="text-red-500">*</span>
                        )}
                      </label>
                      {user.str_confirm_password && user.str_password !== user.str_confirm_password && (
                        <span className="text-[11px] font-semibold text-red-500">
                          Mismatch
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder={isEdit ? "Re-enter new password" : "Re-enter password"}
                        value={user.str_confirm_password}
                        onChange={(e) => setUser((p) => ({ ...p, str_confirm_password: e.target.value }))}
                        className={`w-full h-10 px-3.5 pr-9 rounded-lg text-sm transition-all ${user.str_confirm_password && user.str_password !== user.str_confirm_password
                          ? "bg-white border border-red-400 focus:ring-2 focus:ring-red-400/20"
                          : "bg-white border border-gray-200 focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a]"
                          }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        {showConfirmPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Link Messaging Accounts */}
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Link Messaging Account(s) <span className="text-gray-400 font-normal">(Optional tag binding)</span>
                    </label>
                    <AccountTagPicker
                      selected={user.linked_accounts}
                      onChange={(val) => setUser((p) => ({ ...p, linked_accounts: val }))}
                      allAccounts={allAccounts}
                    />
                  </div>

                  {/* Two-Factor Authentication (2FA) */}
                  <div className="sm:col-span-2">
                    <CleanToggle
                      checked={Boolean(user.bool_two_factor_enabled)}
                      onChange={(val) => setUser((p) => ({ ...p, bool_two_factor_enabled: val }))}
                      label="Two-Factor Authentication (2FA)"
                      desc="Require a 6-digit security verification code on sign-in after CAPTCHA verification"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ════ STEP 3: Country Access ════ */}
            {step === 3 && (
              <div className="space-y-4">
                <CountryTagPicker
                  selected={selectedCountries}
                  onChange={setSelectedCountries}
                  allCountries={countries}
                />
              </div>
            )}

            {/* ════ STEP 4: Account Settings & Summary ════ */}
            {step === 4 && (
              <div className="space-y-3.5">
                <div className="space-y-3">
                  <CleanToggle
                    checked={settings.bool_bot}
                    onChange={(v) => setSettings((p) => ({ ...p, bool_bot: v }))}
                    label="WhatsApp Automation Bot"
                    desc="Enable dynamic auto-replies, flows & interactive responses"
                  />

                  <CleanToggle
                    checked={settings.bool_chat_enabled}
                    onChange={(v) => setSettings((p) => ({ ...p, bool_chat_enabled: v }))}
                    label="WhatsApp Live Chat Access"
                    desc="Allow this user account to access the two-way live customer chat console (Default: Enabled)"
                  />

                  <CleanToggle
                    checked={settings.bool_indiamart_enabled}
                    onChange={(v) => setSettings((p) => ({ ...p, bool_indiamart_enabled: v }))}
                    label="IndiaMART CRM & Integrations Access"
                    desc="Allow this user to access IndiaMART CRM sync, Webhook, and automated Thank-You replies (Default: Disabled)"
                  />

                  <CleanToggle
                    checked={settings.bool_image_camp_enabled}
                    onChange={(v) => setSettings((p) => ({ ...p, bool_image_camp_enabled: v }))}
                    label="Image Camp & Dynamic Graphics Access"
                    desc="Allow this user to design dynamic image templates and send personalized image broadcast campaigns (Default: Disabled)"
                  />

                  <CleanToggle
                    checked={settings.bool_tiny_url_enabled}
                    onChange={(v) => setSettings((p) => ({ ...p, bool_tiny_url_enabled: v }))}
                    label="Tiny URLs Shortener Access"
                    desc="Allow this user to create tracked short links, UTM tags, and review click analytics (Default: Disabled)"
                  />

                  <CleanToggle
                    checked={settings.bool_embedded_signup_enabled}
                    onChange={(v) => setSettings((p) => ({ ...p, bool_embedded_signup_enabled: v }))}
                    label="Meta WhatsApp Embedded Signup Access"
                    desc="Allow this user account to onboard WhatsApp numbers using Meta Embedded Signup (Cloud API)"
                  />

                  {settings.bool_embedded_signup_enabled && (
                    <div className="p-3.5 bg-emerald-50/60 dark:bg-emerald-950/20 rounded-xl border border-emerald-200 dark:border-emerald-900/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <label className="block text-xs font-bold text-emerald-950 dark:text-emerald-300">
                          Allowed Embedded Signup Limit (Times)
                        </label>
                        <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400">
                          How many times this user can connect numbers (Used: {settings.int_embedded_signup_used || 0})
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="999"
                          value={
                            settings.int_embedded_signup_limit !== undefined && settings.int_embedded_signup_limit !== null
                              ? settings.int_embedded_signup_limit
                              : 0
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            setSettings((p) => ({
                              ...p,
                              int_embedded_signup_limit: val === "" ? 0 : Math.max(0, parseInt(val, 10) || 0),
                            }));
                          }}
                          className="w-24 p-2 text-xs font-bold font-mono bg-white dark:bg-gray-900 border border-emerald-300 dark:border-emerald-800 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 text-center"
                        />
                        <span className="text-xs text-emerald-800 dark:text-emerald-300 font-medium">times</span>
                      </div>
                    </div>
                  )}

                  <CleanToggle
                    checked={settings.bool_ip_whitelist_enabled}
                    onChange={(v) => setSettings((p) => ({ ...p, bool_ip_whitelist_enabled: v }))}
                    label="IP Whitelisting Access Control"
                    desc="Restrict incoming REST API transmissions to authorized server IPs"
                  />

                  {settings.bool_ip_whitelist_enabled && (
                    <div className="p-3.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Allowed IP List (one per line or comma-separated)
                      </label>
                      <textarea
                        rows={2}
                        placeholder={"192.168.1.1\n203.0.113.0/24"}
                        value={settings.str_allow_ip_list}
                        onChange={(e) => setSettings((p) => ({ ...p, str_allow_ip_list: e.target.value }))}
                        className="w-full p-2.5 text-xs font-mono bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg outline-none resize-none focus:ring-2 focus:ring-[#0f172a]/20 focus:border-[#0f172a]"
                      />
                    </div>
                  )}
                </div>

                {/* Provisioning Summary Card */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-bold text-[#0f172a] dark:text-white mb-2.5">
                    📋 Provisioning Confirmation
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs">
                    <div>
                      <span className="text-gray-400">Account:</span>{" "}
                      <span className="font-semibold text-gray-800 dark:text-gray-200 block truncate">
                        {account.str_company_name || "—"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">User:</span>{" "}
                      <span className="font-semibold text-gray-800 dark:text-gray-200 block truncate">
                        {user.str_name || "—"} (@{user.str_username || "—"})
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Role:</span>{" "}
                      <span className="font-semibold text-gray-800 dark:text-gray-200 block">
                        {getRoleDisplayName(user.str_role_id)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">{isEdit ? "Wallet Balance:" : "Initial Credits:"}</span>{" "}
                      <span className="font-semibold text-gray-800 dark:text-gray-200 block font-mono">
                        ₹ {Number(account.int_current_credit || 0).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Operator:</span>{" "}
                      <span className="font-semibold text-gray-800 dark:text-gray-200 block truncate">
                        {operators.find((o) => String(o.int_operator_id) === String(account.int_operator))?.str_operator || "Default"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Country Access:</span>{" "}
                      <span className="font-semibold text-gray-800 dark:text-gray-200 block">
                        {selectedCountries.length > 0 ? `${selectedCountries.length} countries` : "Global (All)"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Embedded Signup:</span>{" "}
                      <span
                        className={`font-semibold block ${
                          settings.bool_embedded_signup_enabled
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-gray-500"
                        }`}
                      >
                        {settings.bool_embedded_signup_enabled
                          ? `Enabled (Limit: ${settings.int_embedded_signup_limit || 1})`
                          : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>

        </div>

        {/* ── Bottom Navigation Row ── */}
        <div className="flex-shrink-0 pt-4 border-t border-gray-150 dark:border-gray-800 flex items-center justify-between">
          <div>
            {isEdit ? (
              <button
                type="button"
                onClick={handleDeleteUser}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-red-200 dark:border-red-900/60 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg text-xs font-bold transition-colors cursor-pointer disabled:opacity-50"
              >
                <TrashIcon className="w-3.5 h-3.5" />
                Delete User Account
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-xs font-semibold text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors cursor-pointer"
              >
                Cancel
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {step < 4 ? (
              <button
                type="button"
                onClick={goNext}
                className="bg-[#0f172a] hover:bg-black text-white px-8 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-xs cursor-pointer"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#0f172a] hover:bg-black text-white px-8 py-2.5 rounded-lg text-sm font-bold transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isEdit ? "Saving..." : "Provisioning..."}
                  </>
                ) : isEdit ? (
                  "Save & Update User"
                ) : (
                  "Confirm & Create"
                )}
              </button>
            )}
          </div>
        </div>

      </div>

    </div>
  );
}
